import {
  AreaChart,
  BarChart,
  LineChart,
  type ChartTooltipStylesNames,
} from '@mantine/charts';
import { useElementSize } from '@mantine/hooks';
import React, { useMemo } from 'react';
import {
  CHART_X_KEY,
  CHAT_CHART_COLORS,
  DisplayableChatChartSpec,
  MIN_TOOLTIP_WIDTH,
  TICK_FONT_PX,
  TILTED_LABEL_ANGLE,
  chartDataRows,
  chartSeriesKey,
  chartUnit,
  chooseChartLayout,
  formatChartAxisTick,
  formatChartValue,
  truncateChartLabel,
} from 'src/app/chat/chatChartFormat';
import { Box } from 'src/design/atoms/Box';

const TOOLTIP_PROPS = { allowEscapeViewBox: { x: false, y: false } };

// The chat panels clip overflow, so the tooltip must fit inside the plot area:
// Mantine's 200px min-width and an unwrapped long label would push it past the right edge.
const tooltipStyles = (
  maxWidth: number,
): Partial<Record<ChartTooltipStylesNames, React.CSSProperties>> => ({
  tooltip: { minWidth: Math.min(MIN_TOOLTIP_WIDTH, maxWidth), maxWidth },
  tooltipLabel: {
    padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-sm)',
    fontSize: 'var(--mantine-font-size-sm)',
    overflowWrap: 'break-word',
  },
  tooltipBody: {
    padding: '0 var(--mantine-spacing-sm) var(--mantine-spacing-xs)',
  },
  tooltipItem: { flexWrap: 'wrap', columnGap: 'var(--mantine-spacing-sm)' },
  tooltipItemBody: { marginInlineEnd: 0, minWidth: 0 },
  tooltipItemName: { overflowWrap: 'break-word' },
  tooltipItemData: { whiteSpace: 'nowrap', marginInlineStart: 'auto' },
});

const CartesianChartBody = React.memo<{
  spec: DisplayableChatChartSpec;
  availableWidth: number;
}>(({ spec, availableWidth }) => {
  const unit = chartUnit(spec);
  const data = useMemo(() => chartDataRows(spec), [spec]);
  const series = useMemo(
    () =>
      spec.series.map((s, index) => ({
        name: chartSeriesKey(index),
        label: s.label,
        color: CHAT_CHART_COLORS[index],
      })),
    [spec.series],
  );

  const layout = useMemo(
    () => chooseChartLayout(spec, availableWidth),
    [spec, availableWidth],
  );
  const styles = useMemo(() => tooltipStyles(layout.tooltipMaxWidth), [layout]);

  const valueTick = (value: number) => formatChartAxisTick(value, unit);
  const valueAxisProps = {
    tickFormatter: valueTick,
    allowDecimals: !layout.integerTicks,
  };
  const shared = {
    h: layout.height,
    w: '100%',
    data,
    dataKey: CHART_X_KEY,
    series,
    withLegend: series.length >= 2,
    withTooltip: true,
    valueFormatter: (value: number) => formatChartValue(value, unit),
    styles,
    tooltipProps: TOOLTIP_PROPS,
  };

  if (layout.kind === 'rows') {
    const barRows = {
      ...shared,
      orientation: 'vertical' as const,
      gridAxis: 'y' as const,
      tickLine: 'x' as const,
      xAxisProps: valueAxisProps,
      yAxisProps: {
        width: layout.categoryAxisWidth,
        interval: 0,
        // Recharts word-wraps a tick given a width; the label is already cut to fit on one line.
        tick: {
          transform: 'translate(-10, 0)',
          fontSize: TICK_FONT_PX,
          fill: 'currentColor',
          width: undefined,
        },
        tickFormatter: (label: string) =>
          truncateChartLabel(label, layout.labelMaxChars),
      },
    };
    return spec.type === 'stacked-bar' ? (
      <BarChart {...barRows} type="stacked" />
    ) : (
      <BarChart {...barRows} />
    );
  }

  const { tilt } = layout;
  const columns = {
    ...shared,
    gridAxis: 'x' as const,
    tickLine: 'y' as const,
    yAxisProps: valueAxisProps,
    xAxisProps: tilt
      ? {
          height: tilt.axisHeight,
          angle: TILTED_LABEL_ANGLE,
          tick: {
            angle: TILTED_LABEL_ANGLE,
            textAnchor: 'end' as const,
            fontSize: TICK_FONT_PX,
            fill: 'currentColor',
          },
          tickFormatter: (label: string) =>
            truncateChartLabel(label, tilt.labelMaxChars),
        }
      : undefined,
  };
  const margin = tilt ? { margin: { left: tilt.leftMargin } } : undefined;

  switch (spec.type) {
    case 'bar':
      return <BarChart {...columns} />;
    case 'stacked-bar':
      return <BarChart {...columns} type="stacked" />;
    case 'line':
      return (
        <LineChart
          {...columns}
          lineChartProps={margin}
          curveType="linear"
          strokeWidth={2}
        />
      );
    case 'area':
      return (
        <AreaChart {...columns} areaChartProps={margin} curveType="linear" />
      );
    default:
      return null;
  }
});

/** @registry Draws a bar, stacked-bar, line, or area AI chat chart, choosing horizontal bars, tilted labels, integer ticks, and tooltip width from the data and the measured chart width. */
export const ChatCartesianChart: React.FC<{
  spec: DisplayableChatChartSpec;
}> = ({ spec }) => {
  const { ref, width } = useElementSize();
  return (
    <Box ref={ref} w="100%">
      <CartesianChartBody spec={spec} availableWidth={width} />
    </Box>
  );
};
